import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../../../api/client";
import { Button, Empty, Icon, Spinner, cx, usePresence } from "../../../components/ui";
import { LabelRaport, STATUS_BULAN } from "../DaftarMurid";
import { NAMA_RUBRIK, namaBulan } from "../logika";
import LihatRaportTL from "./LihatRaportTL";

// ─── Panel rincian satu kelas (team leader) — meluncur dari kanan ───────────
//
// Pola PanelTrainer (Trainer Schedule): dipasang lewat PORTAL ke <body>, tertutup lewat ×,
// klik di luar panel, atau Escape. Isinya: penanggung jawab raport (bisa ditetapkan bila
// trainernya 2+), lalu tiap murid dengan status bulan & raportnya, pilihan kelas utama untuk
// murid yang menunggu, dan raport terbitnya (baca, cetak, batalkan).

export default function PanelKelasTL({ classId, termId, buka, onTutup, onUbah, setToast }) {
  const { terpasang, closing } = usePresence(buka, 200);
  const [data, setData] = useState(null);
  const [galat, setGalat] = useState("");
  const [pemegang, setPemegang] = useState(null);
  const [lihat, setLihat] = useState(null);          // studentId yang raport terbitnya dibuka
  const [sibuk, setSibuk] = useState(false);
  const tombolTutup = useRef(null);
  const tutup = useRef(onTutup);
  const adaModal = useRef(false);
  const nomor = useRef(0);
  useEffect(() => { tutup.current = onTutup; });
  useEffect(() => { adaModal.current = !!lihat; }, [lihat]);

  const muat = useCallback(() => {
    if (!classId || !termId) return;
    const ini = ++nomor.current;
    api.getRaportProgresKelas(classId, termId)
      .then((r) => {
        if (ini !== nomor.current) return;
        setData(r);
        setGalat("");
        setPemegang(r.pemegang.id);
      })
      .catch((err) => { if (ini === nomor.current) setGalat(err.message || "Rincian kelas gagal dimuat."); });
  }, [classId, termId]);

  useEffect(() => {
    if (!buka) return;
    setData(null);
    setLihat(null);
    muat();
  }, [buka, muat]);

  useEffect(() => {
    if (!buka) return undefined;
    const asal = document.activeElement;
    // Escape menutup panel — kecuali raport terbit sedang dibuka di atasnya.
    const tekan = (e) => { if (e.key === "Escape" && !adaModal.current) tutup.current(); };
    document.addEventListener("keydown", tekan);
    if (tombolTutup.current) tombolTutup.current.focus();
    return () => {
      document.removeEventListener("keydown", tekan);
      if (asal && typeof asal.focus === "function" && document.contains(asal)) asal.focus();
    };
  }, [buka]);

  const jalankan = async (panggil, pesanGagal) => {
    setSibuk(true);
    try {
      const r = await panggil();
      if (setToast) setToast({ type: "success", message: r.message || "Tersimpan." });
      muat();
      onUbah();
    } catch (err) {
      if (setToast) setToast({ type: "error", message: err.message || pesanGagal });
    } finally {
      setSibuk(false);
    }
  };

  if (!terpasang) return null;
  const k = data && data.kelas;
  const muridLihat = lihat && data ? data.murid.find((m) => m.studentId === lihat) : null;

  return (
    <>
      {createPortal(
        <div
          className={cx("fixed inset-0 z-[999] bg-black/40", closing ? "anim-fade-out" : "anim-fade")}
          onMouseDown={(e) => e.target === e.currentTarget && tutup.current()}
        >
          <aside
            role="dialog"
            aria-modal="true"
            aria-labelledby="rp-tl-panel-judul"
            className={cx(
              "absolute right-0 top-0 h-full w-full max-w-[560px] overflow-y-auto bg-white shadow-2xl dark:bg-slate-900",
              closing ? "anim-panel-out" : "anim-panel-in"
            )}
          >
            <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-slate-100 bg-white/95 p-5 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
              <div className="min-w-0">
                <h2 id="rp-tl-panel-judul" className="truncate text-xl font-black text-slate-900 dark:text-white">
                  {k ? k.nama : "Rincian kelas"}
                </h2>
                {k && (
                  <p className="mt-0.5 text-sm text-slate-500">
                    {[k.program, k.level, k.sesi].filter(Boolean).join(" · ")} · {NAMA_RUBRIK[k.rubrik]}
                  </p>
                )}
              </div>
              <button
                ref={tombolTutup}
                type="button"
                aria-label="Tutup rincian kelas"
                onClick={() => tutup.current()}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-white"
              >
                <Icon name="close" />
              </button>
            </div>

            <div className="space-y-6 p-5">
              {galat ? (
                <Empty label={galat} />
              ) : !data ? (
                <Spinner label="Memuat rincian kelas…" />
              ) : (
                <>
                  <section aria-label="Penanggung jawab raport">
                    <h3 className="text-xs font-black uppercase tracking-wide text-slate-500">Penanggung jawab raport</h3>
                    {data.trainer.length === 0 ? (
                      <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">Kelas ini belum punya trainer di jadwalnya.</p>
                    ) : data.trainer.length === 1 ? (
                      <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                        <b>{data.trainer[0].nama}</b> — otomatis, satu-satunya trainer kelas ini.
                      </p>
                    ) : (
                      <div className="mt-2">
                        {data.pemegang.status === "belum-ditetapkan" && (
                          <p className="mb-2 text-sm font-bold text-amber-700 dark:text-amber-300">
                            Belum ditetapkan — sampai dipilih, raport kelas ini tak bisa diisi siapa pun.
                          </p>
                        )}
                        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Pilih penanggung jawab">
                          {data.trainer.map((t) => (
                            <button
                              key={t.id}
                              type="button"
                              role="radio"
                              aria-checked={pemegang === t.id}
                              onClick={() => setPemegang(t.id)}
                              className={cx(
                                "lift-press rounded-xl border px-3 py-2 text-sm font-bold",
                                pemegang === t.id
                                  ? "border-primary bg-primary text-white"
                                  : "border-slate-200 text-slate-700 hover:border-primary/60 dark:border-slate-700 dark:text-slate-200"
                              )}
                            >
                              {t.nama}
                            </button>
                          ))}
                        </div>
                        <Button
                          className="mt-3"
                          disabled={sibuk || !pemegang || pemegang === data.pemegang.id}
                          onClick={() => jalankan(() => api.tetapkanPemegangRaport(k.id, pemegang), "Penanggung jawab gagal ditetapkan.")}
                        >
                          <Icon name="how_to_reg" />
                          Tetapkan penanggung jawab
                        </Button>
                      </div>
                    )}
                  </section>

                  <section aria-label="Murid">
                    <h3 className="text-xs font-black uppercase tracking-wide text-slate-500">Murid ({data.murid.length})</h3>
                    {!data.murid.length ? (
                      <p className="mt-2 text-sm text-slate-500">Belum ada murid di kelas ini.</p>
                    ) : (
                      <ul className="mt-2 divide-y divide-slate-100 dark:divide-slate-800">
                        {data.murid.map((m) => (
                          <BarisMurid
                            key={m.studentId}
                            m={m}
                            term={data.term}
                            sibuk={sibuk}
                            onLihat={() => setLihat(m.studentId)}
                            onPilihKelasUtama={(c) => jalankan(
                              () => api.pilihKelasUtamaRaport(data.term.id, m.studentId, c),
                              "Kelas utama gagal ditetapkan."
                            )}
                          />
                        ))}
                      </ul>
                    )}
                  </section>
                </>
              )}
            </div>
          </aside>
        </div>,
        document.body
      )}
      {muridLihat && (
        <LihatRaportTL
          murid={muridLihat}
          onTutup={() => setLihat(null)}
          onUbah={() => { muat(); onUbah(); }}
          setToast={setToast}
        />
      )}
    </>
  );
}

function BarisMurid({ m, term, sibuk, onLihat, onPilihKelasUtama }) {
  return (
    <li className="py-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate font-bold text-slate-900 dark:text-white">{m.nama}</p>
          <p className="text-xs text-slate-500">{m.studentId}</p>
          {m.alasan === "kelas-utama-lain" && (
            <p className="mt-1 text-xs text-slate-500">Raportnya diisi di kelas {m.kelasUtama ? m.kelasUtama.nama : "lain"}.</p>
          )}
          {m.alasan === "beda-cabang" && (
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">Terdaftar di cabang lain; raportnya mengikuti term cabangnya.</p>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className="flex items-center gap-1.5">
            {m.bulan.map((s, i) => (
              <span
                key={i}
                title={`Bulan ${i + 1} (${namaBulan(term.bulan[i])}): ${STATUS_BULAN[s].label}`}
                aria-label={`Bulan ${i + 1} ${STATUS_BULAN[s].label}`}
                className={cx("inline-block h-3 w-3 rounded-full border-2", STATUS_BULAN[s].titik)}
              />
            ))}
          </span>
          {!m.alasan && <LabelRaport m={m} />}
          {m.alasan === "menunggu-kelas-utama" && (
            <span className="text-[11px] font-bold text-amber-700 dark:text-amber-300">Menunggu kelas utama</span>
          )}
        </div>
      </div>
      {m.alasan === "menunggu-kelas-utama" && (
        <div className="mt-2 flex flex-wrap items-center gap-2" role="group" aria-label={`Kelas utama ${m.nama}`}>
          <span className="text-xs text-slate-500">Pilih kelas utama:</span>
          {m.kandidat.map((c) => (
            <button
              key={c.classId}
              type="button"
              disabled={sibuk}
              onClick={() => onPilihKelasUtama(c.classId)}
              className="lift-press rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-bold text-slate-700 hover:border-primary hover:text-primary disabled:opacity-50 dark:border-slate-700 dark:text-slate-200"
            >
              {c.nama}
            </button>
          ))}
        </div>
      )}
      {m.riwayat.length > 0 && (
        <button
          type="button"
          onClick={onLihat}
          className="mt-2 inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-bold text-primary hover:bg-primary/10 dark:text-[#3987e5]"
        >
          <Icon name="description" className="text-sm" />
          Raport terbit ({m.riwayat.length} versi)
        </button>
      )}
    </li>
  );
}
